import { API_BASE_URL, ROUTES } from "../constants/index";
import { getRuntimeConfig } from "./config";

/** First URL segment for each in-app route (e.g. "login" from "/login"). */
const KNOWN_APP_ROUTE_SEGMENTS = new Set(
  Object.values(ROUTES)
    .map((route) => route.replace(/#.*$/, "").split("/").filter(Boolean)[0]?.toLowerCase())
    .filter((segment): segment is string => Boolean(segment))
);

/**
 * When the SPA is hosted under a subfolder (e.g. /UAT) but BASE_PATH was not set,
 * infer it from the first pathname segment if that segment is not an app route.
 */
const detectBasePathFromUrl = (): string => {
  if (typeof window === "undefined") {
    return "/";
  }

  if (typeof window.__APP_BASE__ === "string" && window.__APP_BASE__) {
    return window.__APP_BASE__;
  }

  const segments = window.location.pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "/";
  }

  const first = segments[0].toLowerCase();
  if (KNOWN_APP_ROUTE_SEGMENTS.has(first)) {
    return "/";
  }

  return `/${segments[0]}/`;
};

export const getBasePath = (): string => {
  let rawBasePath = "";
  try {
    rawBasePath = getRuntimeConfig<string>("BASE_PATH", "");
  } catch {
    rawBasePath = "";
  }

  if (!rawBasePath || rawBasePath.trim() === "") {
    return detectBasePathFromUrl();
  }

  let normalized = rawBasePath.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (!normalized.endsWith("/")) {
    normalized = `${normalized}/`;
  }

  return normalized;
};

export const getBasePathWithoutTrailingSlash = (): string => {
  const basePath = getBasePath();
  return basePath === "/" ? "" : basePath.slice(0, -1);
};

/**
 * Public site root for share links and QR codes (includes /UAT when deployed there).
 * Prefer QRCARD_URL from config.js; otherwise origin + BASE_PATH.
 */
export function getPublicAppBaseUrl(): string {
  if (typeof window === "undefined") {
    return "";
  }

  const fromConfig = String(getRuntimeConfig("QRCARD_URL", "")).trim();
  if (fromConfig) {
    return fromConfig.replace(/\/+$/, "");
  }

  const base = getBasePathWithoutTrailingSlash();
  return `${window.location.origin}${base}`;
}

export type PublicProfileKind = "employee" | "individual";

/** Full URL for a public profile (used in QR codes). */
export function getPublicProfileUrl(
  encryptedKey: string,
  kind: PublicProfileKind = "employee"
): string {
  const base = getPublicAppBaseUrl().replace(/\/+$/, "");
  const segment = kind === "individual" ? "individuals" : "employees";
  return `${base}/${segment}/${encodeURIComponent(encryptedKey)}`;
}

/** Full URL for a public employee profile (used in QR codes). */
export function getPublicEmployeeProfileUrl(encryptedKey: string): string {
  return getPublicProfileUrl(encryptedKey, "employee");
}

/** Full URL for a public individual profile (used in QR codes). */
export function getPublicIndividualProfileUrl(encryptedKey: string): string {
  return getPublicProfileUrl(encryptedKey, "individual");
}

/**
 * Live vCard URL for NFC tags — tapping opens Add Contact with up-to-date details.
 */
export function getPublicContactVcfUrl(
  encryptedKey: string,
  kind: PublicProfileKind = "employee",
  source: "nfc" | "vcard" = "nfc"
): string {
  const apiBase = API_BASE_URL.replace(/\/+$/, "");
  const controller = kind === "individual" ? "individual" : "employee";
  return `${apiBase}/${controller}/Encrypted/${encodeURIComponent(encryptedKey)}/contact.vcf?source=${source}`;
}

/** Path to a static file in `public/` (respects BASE_PATH when deployed under e.g. /UAT). */
export function getPublicAssetUrl(assetPath: string): string {
  const normalized = assetPath.startsWith("/") ? assetPath : `/${assetPath}`;
  const base = getBasePathWithoutTrailingSlash();
  const full = `${base}${normalized}`.replace(/\/{2,}/g, "/");
  return full.startsWith("/") ? full : `/${full}`;
}

export function getBrandLogoUrl(): string {
  return getPublicAssetUrl("/logo.webp");
}
